import { formatCurrency, formatDateTime } from './salesUtils.js'

// ==================== SHIFT CALCULATIONS ====================

/**
 * Calculate total cash flow for a shift
 * @param {Object} shift - Shift data
 * @returns {Object} Cash flow summary
 */
export const calculateShiftCashFlow = (shift) => {
  const {
    opening_cash = 0,
    total_cash_sales = 0,
    total_non_cash_sales = 0,
    total_cash_in = 0,
    total_cash_out = 0,
    closing_cash = 0
  } = shift

  const expectedCash = opening_cash + total_cash_sales + total_cash_in - total_cash_out
  const cashDifference = closing_cash - expectedCash
  const grandTotal = total_cash_sales + total_non_cash_sales

  return {
    opening_cash,
    total_cash_sales,
    total_non_cash_sales,
    total_cash_in,
    total_cash_out,
    closing_cash,
    expected_cash: expectedCash,
    cash_difference: cashDifference,
    grand_total: grandTotal,
    net_cash_flow: total_cash_in - total_cash_out
  }
}

/**
 * Calculate shift performance metrics
 * @param {Object} shift - Shift data
 * @returns {Object} Performance metrics
 */
export const calculateShiftMetrics = (shift) => {
  const cashFlow = calculateShiftCashFlow(shift)
  const {
    grand_total,
    total_cash_sales,
    total_non_cash_sales,
    cash_difference,
    opening_cash
  } = cashFlow

  // Calculate growth percentage compared to opening cash
  const growthPercentage = opening_cash > 0 
    ? ((grand_total - opening_cash) / opening_cash) * 100 
    : 0

  // Calculate cash vs non-cash ratio
  const cashRatio = grand_total > 0 
    ? (total_cash_sales / grand_total) * 100 
    : 0

  // Determine shift status based on cash difference
  const shiftStatus = Math.abs(cash_difference) <= 5000 ? 'BALANCED' : 
                     cash_difference > 5000 ? 'SURPLUS' : 'DEFICIT'

  return {
    growthPercentage: Number(growthPercentage.toFixed(2)),
    cashRatio: Number(cashRatio.toFixed(2)),
    shiftStatus,
    totalTransactions: grand_total,
    averageTransaction: grand_total / Math.max(1, shift.transaction_count || 1)
  }
}

// ==================== FORMATTING FUNCTIONS ====================

/**
 * Format shift summary for display
 * @param {Object} shift - Shift data
 * @returns {Object} Formatted shift summary
 */
export const formatShiftSummary = (shift) => {
  const cashFlow = calculateShiftCashFlow(shift)
  const metrics = calculateShiftMetrics(shift)

  return {
    id: shift.id,
    shiftNumber: shift.shift_number,
    status: shift.status,
    duration: formatShiftDuration(shift.opened_at, shift.closed_at),
    openedBy: shift.opened_by_name,
    closedBy: shift.closed_by_name,
    openedAt: formatDateTime(shift.opened_at),
    closedAt: shift.closed_at ? formatDateTime(shift.closed_at) : null,
    
    // Formatted cash amounts
    openingCash: formatCurrency(cashFlow.opening_cash),
    closingCash: formatCurrency(cashFlow.closing_cash),
    expectedCash: formatCurrency(cashFlow.expected_cash),
    cashDifference: formatCurrency(Math.abs(cashFlow.cash_difference)),
    grandTotal: formatCurrency(cashFlow.grand_total),
    
    // Cash flow breakdown
    totalCashSales: formatCurrency(cashFlow.total_cash_sales),
    totalNonCashSales: formatCurrency(cashFlow.total_non_cash_sales),
    totalCashIn: formatCurrency(cashFlow.total_cash_in),
    totalCashOut: formatCurrency(cashFlow.total_cash_out),
    
    // Metrics
    growthPercentage: `${metrics.growthPercentage}%`,
    cashRatio: `${metrics.cashRatio}%`,
    
    // Status info
    statusInfo: getShiftStatusInfo(shift.status),
    differenceInfo: getCashDifferenceInfo(cashFlow.cash_difference),
    
    // Raw values for calculations
    raw: cashFlow
  }
}

/**
 * Format cash entry for display
 * @param {Object} entry - Cash entry data
 * @returns {Object} Formatted cash entry
 */
export const formatCashEntry = (entry) => {
  const typeInfo = getCashEntryTypeInfo(entry.type)
  
  return {
    id: entry.id,
    type: entry.type,
    amount: formatCurrency(entry.amount),
    description: entry.description,
    createdBy: entry.created_by_name,
    createdAt: formatDateTime(entry.created_at),
    typeInfo,
    rawAmount: entry.amount
  }
}

/**
 * Format shift duration
 * @param {string} openedAt - Opening timestamp
 * @param {string} closedAt - Closing timestamp (optional)
 * @returns {string} Formatted duration
 */
export const formatShiftDuration = (openedAt, closedAt = null) => {
  const start = new Date(openedAt)
  const end = closedAt ? new Date(closedAt) : new Date()
  
  const diffMs = end - start
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
  
  if (diffHours === 0) {
    return `${diffMinutes} menit`
  } else if (diffHours < 24) {
    return `${diffHours} jam ${diffMinutes} menit`
  } else {
    const days = Math.floor(diffHours / 24)
    const remainingHours = diffHours % 24
    return `${days} hari ${remainingHours} jam`
  }
}

// ==================== STATUS AND TYPE HELPERS ====================

/**
 * Get shift status information
 * @param {string} status - Shift status
 * @returns {Object} Status info with styling
 */
export const getShiftStatusInfo = (status) => {
  const statusConfig = {
    'OPEN': {
      label: 'Shift Buka',
      shortLabel: 'Buka',
      color: 'bg-green-100 text-green-800 border-green-200',
      iconColor: 'text-green-600',
      badgeColor: 'bg-green-500'
    },
    'CLOSED': {
      label: 'Shift Tutup',
      shortLabel: 'Tutup',
      color: 'bg-gray-100 text-gray-800 border-gray-200',
      iconColor: 'text-gray-600',
      badgeColor: 'bg-gray-500'
    }
  }
  
  return statusConfig[status] || {
    label: status,
    shortLabel: status,
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    iconColor: 'text-gray-600',
    badgeColor: 'bg-gray-500'
  }
}

/**
 * Get cash difference status information
 * @param {number} cashDifference - Cash difference amount
 * @returns {Object} Difference status info
 */
export const getCashDifferenceInfo = (cashDifference) => {
  const tolerance = 5000 // 5000 IDR tolerance
  
  if (Math.abs(cashDifference) <= tolerance) {
    return {
      status: 'BALANCED',
      label: 'Seimbang',
      color: 'bg-green-100 text-green-800 border-green-200',
      iconColor: 'text-green-600',
      description: 'Kas seimbang'
    }
  } else if (cashDifference > tolerance) {
    return {
      status: 'SURPLUS',
      label: 'Lebih',
      color: 'bg-blue-100 text-blue-800 border-blue-200',
      iconColor: 'text-blue-600',
      description: `Kelebihan kas ${formatCurrency(cashDifference)}`
    }
  } else {
    return {
      status: 'DEFICIT',
      label: 'Kurang',
      color: 'bg-red-100 text-red-800 border-red-200',
      iconColor: 'text-red-600',
      description: `Kekurangan kas ${formatCurrency(Math.abs(cashDifference))}`
    }
  }
}

/**
 * Get cash entry type information
 * @param {string} type - Cash entry type
 * @returns {Object} Type info with styling
 */
export const getCashEntryTypeInfo = (type) => {
  const typeConfig = {
    'CASH_IN': {
      label: 'Kas Masuk',
      shortLabel: 'Masuk',
      color: 'bg-green-100 text-green-800 border-green-200',
      iconColor: 'text-green-600',
      symbol: '+',
      icon: 'plus'
    },
    'CASH_OUT': {
      label: 'Kas Keluar',
      shortLabel: 'Keluar',
      color: 'bg-red-100 text-red-800 border-red-200',
      iconColor: 'text-red-600',
      symbol: '-',
      icon: 'minus'
    }
  }
  
  return typeConfig[type] || {
    label: type,
    shortLabel: type,
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    iconColor: 'text-gray-600',
    symbol: '',
    icon: 'help'
  }
}

// ==================== VALIDATION HELPERS ====================

/**
 * Check if shift can be closed
 * @param {Object} shift - Shift data
 * @returns {Object} Validation result
 */
export const canCloseShift = (shift) => {
  const errors = []
  
  if (!shift) {
    errors.push('Shift tidak ditemukan')
    return { canClose: false, errors }
  }
  
  if (shift.status !== 'OPEN') {
    errors.push('Shift sudah ditutup')
  }
  
  // You can add more business rules here
  // e.g., minimum shift duration, pending transactions, etc.
  
  return {
    canClose: errors.length === 0,
    errors
  }
}

/**
 * Check if cash entry can be added
 * @param {Object} shift - Shift data
 * @returns {Object} Validation result
 */
export const canAddCashEntry = (shift) => {
  const errors = []
  
  if (!shift) {
    errors.push('Shift tidak ditemukan')
    return { canAdd: false, errors }
  }
  
  if (shift.status !== 'OPEN') {
    errors.push('Tidak dapat menambah kas pada shift yang sudah tutup')
  }
  
  return {
    canAdd: errors.length === 0,
    errors
  }
}

// ==================== SUMMARY GENERATORS ====================

/**
 * Generate shift summary report
 * @param {Object} shift - Shift data
 * @returns {Object} Comprehensive shift report
 */
export const generateShiftReport = (shift) => {
  const summary = formatShiftSummary(shift)
  const metrics = calculateShiftMetrics(shift)
  const cashFlow = calculateShiftCashFlow(shift)
  
  return {
    header: {
      shiftNumber: summary.shiftNumber,
      date: summary.openedAt,
      duration: summary.duration,
      operator: summary.openedBy,
      status: summary.statusInfo
    },
    
    cashSummary: {
      opening: cashFlow.opening_cash,
      closing: cashFlow.closing_cash,
      expected: cashFlow.expected_cash,
      difference: cashFlow.cash_difference,
      differenceInfo: getCashDifferenceInfo(cashFlow.cash_difference)
    },
    
    salesSummary: {
      totalSales: cashFlow.grand_total,
      cashSales: cashFlow.total_cash_sales,
      nonCashSales: cashFlow.total_non_cash_sales,
      cashRatio: metrics.cashRatio
    },
    
    cashMovement: {
      cashIn: cashFlow.total_cash_in,
      cashOut: cashFlow.total_cash_out,
      netFlow: cashFlow.net_cash_flow
    },
    
    performance: {
      growth: metrics.growthPercentage,
      status: metrics.shiftStatus,
      averageTransaction: formatCurrency(metrics.averageTransaction)
    },
    
    formatted: summary
  }
}

/**
 * Generate daily shift summary from multiple shifts
 * @param {Array} shifts - Array of shifts for a day
 * @returns {Object} Daily summary
 */
export const generateDailySummary = (shifts = []) => {
  const totalSales = shifts.reduce((sum, shift) => {
    const cashFlow = calculateShiftCashFlow(shift)
    return sum + cashFlow.grand_total
  }, 0)
  
  const totalCashSales = shifts.reduce((sum, shift) => {
    return sum + (shift.total_cash_sales || 0)
  }, 0)
  
  const totalNonCashSales = shifts.reduce((sum, shift) => {
    return sum + (shift.total_non_cash_sales || 0)
  }, 0)
  
  const totalShifts = shifts.length
  const averageSalesPerShift = totalShifts > 0 ? totalSales / totalShifts : 0
  
  return {
    totalShifts,
    totalSales: formatCurrency(totalSales),
    totalCashSales: formatCurrency(totalCashSales),
    totalNonCashSales: formatCurrency(totalNonCashSales),
    averageSalesPerShift: formatCurrency(averageSalesPerShift),
    cashRatio: totalSales > 0 ? ((totalCashSales / totalSales) * 100).toFixed(2) : 0,
    shifts: shifts.map(shift => formatShiftSummary(shift))
  }
}